<script setup lang="ts">
  import useMediaQuery from '../../composables/useMediaQuery/useMediaQuery';
  import Icon from '../Icon/Icon.vue';
  import Logo from '../Logo/Logo.vue';

  export interface AppTopbarProps {
    /**
     * Indicates if the AppSidebar is opened or not for styling purposes
     */
    sidebarOpened?: boolean;
    /**
     * Url for the logo anchor wrapper
     */
    href?: string;
  }

  const props = withDefaults(defineProps<AppTopbarProps>(), {
    sidebarOpened: false,
    href: '/',
  });

  const emit = defineEmits(['toggle-sidebar']);
  const isExtraLargeScreen = useMediaQuery('screen and (min-width: 1321px)');
</script>

<template>
  <header data-test="stash-app-topbar" class="stash-app-topbar absolute left-0 top-0">
    <div
      class="fixed top-0 flex h-topbar w-sidebar items-center pl-3 xl:text-white"
      :class="{
        'z-dialog text-white': props.sidebarOpened,
        'z-[401] text-ice-700': !props.sidebarOpened,
      }"
    >
      <button class="xl:hidden" @click="emit('toggle-sidebar')">
        <Icon name="menu" class="mr-3" aria-label="open sidebar navigation" />
      </button>

      <a :href="props.href" class="flex items-center text-inherit">
        <Logo
          :color="props.sidebarOpened ? 'white' : 'purple'"
          variant="brandmark"
          class="transition-colors duration-500 md:hidden"
        />
        <Logo :color="props.sidebarOpened || isExtraLargeScreen ? 'white' : 'purple'" class="hidden md:block" />
      </a>
    </div>

    <div
      :class="[$attrs.class]"
      class="fixed top-0 z-page flex h-topbar w-full flex-row-reverse items-center justify-between bg-white py-3 pl-sidebar pr-3 shadow-md"
    >
      <div class="flex items-center">
        <!-- @slot Actions slot, for showing any actions to the left of the avatar -->
        <slot name="actions"></slot>
        <div class="ml-6">
          <!-- @slot Avatar slot, for showing the avatar on the far right of the header -->
          <slot name="avatar"></slot>
        </div>
      </div>
      <div class="hidden pl-3 xl:block">
        <!-- @slot Text slot, for showing informative texts on the left of large viewports -->
        <slot name="text"></slot>
      </div>
    </div>
  </header>
</template>
